/**
 * MatchPanel — the screener's own verdict for one bar, condition by condition.
 *
 * This is the part that answers "why did it match here and not the day
 * before". The conditions are the boolean flags the screener already emits in
 * its metadata, evaluated on every bar rather than only on the ones that
 * passed, so the panel also works on the near-misses.
 *
 * Falls back to the signal bar when the pointer is not over the chart, so
 * there is always something to read.
 */
import { Activity, CheckCircle2, XCircle } from 'lucide-react';

function Condition({ condition }) {
  const Icon = condition.passed ? CheckCircle2 : XCircle;
  return (
    <li className="flex items-center gap-2 py-0.5">
      <Icon
        size={13}
        className={condition.passed ? 'text-win' : 'text-loss'}
        aria-hidden
      />
      <span
        className={`text-xs ${
          condition.passed ? 'text-foreground' : 'text-muted-foreground'
        }`}
      >
        {condition.label}
      </span>
      <span className="sr-only">{condition.passed ? 'pass' : 'fail'}</span>
    </li>
  );
}

export default function MatchPanel({ evaluation, date, blockers, isSignalBar }) {
  if (!evaluation) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
        <Activity size={14} />
        No evaluation recorded for {date || 'this bar'}.
      </div>
    );
  }

  const { passed, score, conditions = [], error } = evaluation;
  const total = conditions.length;
  const met = conditions.filter((c) => c.passed).length;

  return (
    <div
      className={`rounded-md border px-3 py-2 ${
        error
          ? 'border-loss/30 bg-loss/5'
          : passed
            ? 'border-win/30 bg-win/5'
            : 'border-border bg-muted/20'
      }`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="flex items-baseline gap-2">
          <span className="font-mono text-xs font-semibold text-foreground">
            {date}
          </span>
          {isSignalBar && (
            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">
              signal
            </span>
          )}
        </span>
        <span className="flex items-baseline gap-2">
          {total > 0 && (
            <span className="font-mono text-[11px] text-muted-foreground">
              {met}/{total} conditions
            </span>
          )}
          {score != null && (
            <span className="font-mono text-[11px] text-muted-foreground">
              score {score}
            </span>
          )}
          <span
            className={`text-xs font-bold ${
              error ? 'text-loss' : passed ? 'text-win' : 'text-muted-foreground'
            }`}
          >
            {error ? 'error' : passed ? 'MATCH' : 'no match'}
          </span>
        </span>
      </div>

      {error && (
        <p className="mt-1 font-mono text-[10px] leading-snug text-loss">
          {error}
        </p>
      )}

      {total > 0 && (
        <ul className="mt-1.5 grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          {conditions.map((condition) => (
            <Condition key={condition.key} condition={condition} />
          ))}
        </ul>
      )}

      {blockers?.length > 0 && (
        <p className="mt-1.5 text-[10px] leading-snug text-muted-foreground">
          The engine skipped this bar — {blockers.join(', ')} not ready. The
          verdict above is this chart replaying the screener anyway.
        </p>
      )}
    </div>
  );
}
